import * as Matrix from '../matrix';
import type { Size } from '../util';
import type { PlaneAxis } from './../dimension';
import { Vector } from './../vector';
import * as CameraCalibration from './camera.calibration';
import * as Project from './coordinate.projection';
import type { GeometryCreationContext } from './geometry.factory';
import * as GeoUtil from './geometry.util';

export interface Point {
	x: number;
	y: number;
}
export interface ZPoint extends Point {
	z: number;
}
export interface GeometryCalibration {
	translation: ZPoint;
	center: Point;
	distortion: number[];
	rotation: ZPoint;
	focal: number;
}

export interface GeometryConstructor {
	planeNormal_MRW: Vector;
	planeCenter_MRW: Vector;
	planeOrigin_MRW: Vector;
	planeAxis_MP: PlaneAxis;
	focalLength?: number;
	calibration?: GeometryCalibration;
	homography: number[];
	ctx: GeometryCreationContext;
	createdBy: Geometry3DCreationMethod;
}

export enum Geometry3DCreationMethod {
	ALIGNMENT_GRID,
	PITCH,
}

/**
 * All variables in this file will have different suffixes based on what coordinate system they are in
 *
 * SC  - Screen Coordinates (2d)
 * CI  - Calibrated Image (2d)
 * IP  - Image Plane (2d) - Measured in Pixels with 0,0 at the center of the Raw Bitmap
 * MRW - Measurement Real World (3d)
 * MP  - Measurement Plane (2d)
 *
 */
export class Geometry3D {
	private planeNormal_MRW: Vector;
	private planeCenter_MRW: Vector;
	private planeAxis_MP: PlaneAxis;
	private planeOrigin_MRW: Vector;

	private distance: number; // Distance in Meters
	private focalLength: number;
	private calibration: GeometryCalibration;
	private rotation: number[];
	private resolution: Size;

	private homography: number[];

	constructor(ctx: GeometryConstructor) {
		this.planeCenter_MRW = ctx.planeCenter_MRW;
		this.planeNormal_MRW = ctx.planeNormal_MRW;
		this.planeAxis_MP = ctx.planeAxis_MP;
		this.planeOrigin_MRW = ctx.planeOrigin_MRW;

		this.distance = ctx.ctx.distance;
		this.resolution = ctx.ctx.resolution;
		this.focalLength = ctx.focalLength;
		this.calibration = ctx.calibration;
		if (this.calibration) {
			var rotation = this.calibration.rotation;
			this.rotation = Matrix.createRotationMatrix(rotation.x, rotation.y, rotation.z);
		}

		this.homography = ctx.homography;
	}

	public getHomography(): number[] {
		return this.homography;
	}

	public getDistance(): number {
		return this.distance;
	}

	public getFocalLength(): number {
		return this.focalLength;
	}

	public getPlaneNormal(): Vector {
		return this.planeNormal_MRW;
	}

	public getPlaneCenter(): Vector {
		return this.planeCenter_MRW;
	}

	public getPlaneOrigin(): Vector {
		return this.planeOrigin_MRW;
	}

	public getPlaneAxis(): PlaneAxis {
		return this.planeAxis_MP;
	}

	/**
	 * Perspective transform the points of a quad based on a single point being moved.
	 *
	 * @param points Points of quad to wonk.
	 * @param index Index of point that was moved.
	 * @returns {Vector[]} List of location
	 */
	wonkQuad(points: Vector[], index: number): Vector[] {
		var quad: Vector[] = [];
		var POINTS = GeoUtil.POINTS;
		if (index % 2 === 0) {
			quad[POINTS.TOP_LEFT] = this.perspectiveTransform(this.projectCalibratedToImage(points[POINTS.TOP_LEFT]));
			quad[POINTS.BOTTOM_RIGHT] = this.perspectiveTransform(this.projectCalibratedToImage(points[POINTS.BOTTOM_RIGHT]));

			quad[POINTS.TOP_RIGHT] = new Vector(quad[POINTS.BOTTOM_RIGHT].x, quad[POINTS.TOP_LEFT].y);
			quad[POINTS.BOTTOM_LEFT] = new Vector(quad[POINTS.TOP_LEFT].x, quad[POINTS.BOTTOM_RIGHT].y);
		} else {
			quad[POINTS.TOP_RIGHT] = this.perspectiveTransform(this.projectCalibratedToImage(points[POINTS.TOP_RIGHT]));
			quad[POINTS.BOTTOM_LEFT] = this.perspectiveTransform(this.projectCalibratedToImage(points[POINTS.BOTTOM_LEFT]));

			quad[POINTS.TOP_LEFT] = new Vector(quad[POINTS.BOTTOM_LEFT].x, quad[POINTS.TOP_RIGHT].y);
			quad[POINTS.BOTTOM_RIGHT] = new Vector(quad[POINTS.TOP_RIGHT].x, quad[POINTS.BOTTOM_LEFT].y);
		}

		return quad.map(this.projectMeasureToCalibrated, this);
	}

	/**
	 * Move a point on the image by a distance measured in the real world.
	 *
	 * eg move pixel (0, 20) by (2m, 5m)
	 *
	 * @param start_CI Start point
	 * @param moveAmount_MRW Movement amount (Measured in meters)
	 * @returns {Vector} End point on CI.
	 */
	public movePoint(start_CI: Vector, moveAmount_MRW: Vector) {
		var start_MRW = this.projectCalibratedToWorld(start_CI);

		var moveX = this.planeAxis_MP.x.multiply(moveAmount_MRW.x);
		var moveY = this.planeAxis_MP.y.multiply(moveAmount_MRW.y);

		var end_MRW = start_MRW.add(moveX).add(moveY);
		return this.projectWorldToCalibrated(end_MRW);
	}

	/**
	 * Calculate the distance between two points on the Calibrated image.
	 *
	 * @param start_CI Start point
	 * @param end_CI End point
	 * @returns {number} Distance in meters
	 */
	public calculateDistance(start_CI: Vector, end_CI: Vector) {
		let start_MRW = this.projectCalibratedToWorld(start_CI);
		let end_MRW = this.projectCalibratedToWorld(end_CI);
		return start_MRW.subtract(end_MRW).length();
	}

	/**
	 * subtract two image points and return the vector in world projection
	 *
	 * @param start_CI Start vector in image projection
	 * @param end_CI End vector in image projection
	 * @returns {vector} Result vector in world projection
	 */
	public calculateWorldDifferenceVector(start_CI: Vector, end_CI: Vector) {
		let start_MRW = this.projectCalibratedToWorld(start_CI);
		let end_MRW = this.projectCalibratedToWorld(end_CI);
		return start_MRW.subtract(end_MRW);
	}

	/**
	 * Calculate the area of a collection of points on the Calibrated image.
	 *
	 * @param points_CI Start point
	 * @returns {number} Distance in meters
	 */
	public calculateArea(points_CI: Vector[]) {
		var area = 0;
		var points_MP = points_CI.map(this.projectCalibratedToMeasure, this);

		if (points_MP.length < 3) {
			return NaN;
		}

		var backPoint_MP: Vector = points_MP[points_MP.length - 1];

		for (var i = 0; i < points_MP.length; i++) {
			area += (backPoint_MP.x - points_MP[i].x) * (backPoint_MP.y + points_MP[i].y);
			backPoint_MP = points_MP[i];
		}
		return Math.abs(area / 2);
	}

	private perspectiveTransform(p: Vector) {
		var m = this.homography;
		var x = p.x;
		var y = p.y;
		var w = x * m[6] + y * m[7] + m[8];

		if (Math.abs(w) > GeoUtil.EPSILON) {
			w = 1.0 / w;
			var newX = (x * m[0] + y * m[1] + m[2]) * w;
			var newY = (x * m[3] + y * m[4] + m[5]) * w;
			return new Vector(newX, newY);
		}
		return new Vector(0, 0);
	}

	private projectWorldToCalibrated(point_MRW: Vector): Vector {
		var currentPoint_CI = Project.WorldToCalibrated(this.focalLength, this.resolution, point_MRW);

		if (this.calibration == null) {
			return currentPoint_CI;
		}

		// If the point is way outside of the resolution of the image then ignore the radial distortion.
		if (
			currentPoint_CI.x > this.resolution.width ||
			currentPoint_CI.y > this.resolution.height ||
			currentPoint_CI.y < 0 ||
			currentPoint_CI.x < 0
		) {
			return currentPoint_CI;
		}

		return CameraCalibration.Distort(this.calibration, this.resolution, this.rotation, currentPoint_CI);
	}

	private projectCalibratedToWorld(point_CI: Vector): Vector {
		var currentPoint_CI = point_CI;
		if (this.calibration != null) {
			// Only undisort points inside the size of the image.
			if (
				currentPoint_CI.x <= this.resolution.width &&
				currentPoint_CI.y <= this.resolution.height &&
				currentPoint_CI.y >= 0 &&
				currentPoint_CI.x >= 0
			) {
				currentPoint_CI = CameraCalibration.UnDistort(
					this.calibration,
					this.resolution,
					this.rotation,
					currentPoint_CI
				);
			}
		}

		return Project.CalibratedToWorld(
			this.focalLength,
			this.planeCenter_MRW,
			this.planeNormal_MRW,
			this.resolution,
			currentPoint_CI
		);
	}

	private projectCalibratedToImage(point_CI: Vector): Vector {
		return Project.CalibratedToImage(this.resolution, point_CI);
	}

	private projectMeasureToCalibrated(point_MP: Vector): Vector {
		return Project.MeasureToCalibrated(
			this.focalLength,
			this.planeOrigin_MRW,
			this.planeAxis_MP,
			this.resolution,
			point_MP
		);
	}

	private projectCalibratedToMeasure(point_CI: Vector): Vector {
		return this.perspectiveTransform(this.projectCalibratedToImage(point_CI));
	}
}
